import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UID } from 'agora-rtc-sdk-ng';
import _throttle from 'lodash/throttle';
import { useDispatch } from 'react-redux';

import config from 'config';

import {
  ActiveUser,
  ApproveHandRaiseMessage,
  ChannelMessage,
  ChannelMessageType,
  DisableHandRaiseMessage,
  EnableHandRaiseMessage,
  EventUserSchema,
  FacingMode,
  InviteeSchema,
  InviteToStageMessage,
  Layout,
  LiveEventAction,
  LiveEventTokenSource,
  MuteAudioMessage,
  OnStageUser,
  OnStageUserType,
  PeerMessage,
  PeerMessageType,
  PinMode,
  RejectHandRaiseMessage,
  RTCUser,
  UserRole
} from 'types';
import {
  ACTIVE_VOLUME_THRESHOLD,
  convertDateToTimestamp,
  getActualRequests,
  getActualUsers,
  hashUsername,
  mapUserRoleToAgoraClientRole,
  mergeEventUserAndRTCUser
} from 'utils';
import { eventsActions } from 'store/actions';
import {
  activeStreamingPlatformsSelector,
  authSelector,
  eventSessionSelector,
  eventSessionTokenSelector,
  hostsSelector,
  isHandRaisedSelector,
  isHandRaiseEnabledSelector,
  isHostSelector,
  isInvitedToStageSelector,
  isPassiveViewerSelector,
  requestsSelector,
  stageUsersSelector,
  userSelector,
  viewersSelector
} from 'store/selectors';
import { api, LoggerService, tracker } from 'services';
import { useLiveEventActions, useToast } from 'hooks';

import { useStore } from '../utils';

import { useRTC } from './rtc';
import { useRTM } from './rtm';

export type RoomState = {
  ready: boolean;
  loading: boolean;
  joined: boolean;
  videoMuted: boolean;
  audioMuted: boolean;
  facingMode: FacingMode;
  layout: Layout;
  autoLayoutChange: boolean;
  activeUser: ActiveUser;
};

export type RoomOptions = RoomCallbacks & {
  username?: string;
  defaultAudioMuted?: boolean;
  defaultVideoMuted?: boolean;
  ingestServer?: string;
};

export type RoomCallbacks = {
  onJoinedToStage?(): void;
  onHandRaiseApproved?(): void;
  onHandRaiseRejected?(): void;
  onInvitedToStage?(): void;
  onRemovedFromStage?(): void;
  onPermissionDenied?(): void;
  onMuted?(): void;
  onEnded?(): void;
  onAudienceLimitIsReached?(): void;
  onJoiningDeclined?(username: string, name: string, isInvitee: boolean): void;
  onNetworkQualityChanged?(isStable: boolean): void;
  onJoinedFromAnotherSession?(): void;
  onAudioAutoplayBlocked?(): void;
};

export type RoomResult = {
  /**
   * indicates whether the local user is joined the rtc room,
   * and ready to publish local streams, receive remote streams or join the stage
   */
  ready: boolean;
  /**
   * indicates whether the local user is in process of joining to rtm channel,
   * and getting ready for sending or receiving real-time messages
   */
  loading: boolean;
  /**
   * indicates whether the local user is in process of getting joined to stage
   */
  joining: boolean;
  /**
   * indicates whether the local user is joined to stage
   */
  joined: boolean;
  pinIsAvailable: boolean;
  isPassiveViewer: boolean;
  isGridLayoutAvailable: boolean;
  isPinned: boolean;
  handRaised: boolean;
  /**
   * indicates whether the local video is on or off
   */
  videoMuted: boolean;
  /**
   * indicates whether the local audio is on or off
   */
  audioMuted: boolean;
  membersCount: number;
  requestsCount: number;
  totalViewersCount: number;
  anonymousViewersCount: number;
  /**
   * indicates whether the local video is from front or back camera
   */
  facingMode: FacingMode;
  layout: Layout;
  activeSpeaker: OnStageUser | null;
  stageUsers: OnStageUser[];
  requests: EventUserSchema[];
  viewers: EventUserSchema[];
  /**
   * Join to stage passively and
   * This is method is used to watch event passively while on backstage
   */
  preview(): Promise<void>;
  /**
   * Join to stage and become a speaker
   */
  joinStage(): Promise<void>;
  /**
   * Leave the stage and become a viewer.
   * @param withInviteeCode - Is the leaving user using invitation link or not
   */
  leaveStage(withInviteeCode?: boolean): Promise<void>;
  /**
   * Leave the event as a viewer (viewer will be removed from viewers list)
   */
  leaveEvent(): Promise<void>;
  /**
   * Decline joining the stage. Decline has 4 cases:
   *  - when request is accepted by the host/moderator
   *  - when joining the stage with an invitation link
   *  - when existing speaker left the event and trying to rejoin
   */
  declineJoining(isInvitee?: boolean): Promise<void>;
  /**
   * Send request to join the stage
   */
  raiseHand(): Promise<void>;
  /**
   * Cancel sent request to join the stage
   */
  dropHand(): Promise<void>;
  pushStreamToCDN(rtmpURL: string): Promise<void>;
  stopStreamToCDN(rtmpURL: string): Promise<void>;
  publishLocalTracks(audioEnabled: boolean, videoEnabled: boolean): Promise<void>;
  toggleAudio(): Promise<void>;
  toggleVideo(): Promise<void>;
  resumeAudio(): void;
  switchCamera(): Promise<FacingMode>;
  pinActiveSpeaker(username: string | null): void;
  resetInvitationLink(inviteeCode: string): Promise<void>;
  generateInvitationLink(name: string, role: UserRole.SPEAKER | UserRole.MODERATOR): Promise<InviteeSchema>;
  switchRole(username: string, moderator: boolean): Promise<void>;
  removeFromStage(username: string, type?: OnStageUserType): Promise<void>;
  changeActiveSpeaker(username: string | null): void;
  changeLayout(layout: Layout, manual: boolean): void;
  inviteToStage(username: string): Promise<void>;
  approveHandRaise(username: string): Promise<void>;
  rejectHandRaise(username: string): Promise<void>;
  enableHandRaise(): Promise<void>;
  disableHandRaise(): Promise<void>;
  forceMute(username: string): Promise<void>;
  end(): Promise<void>;
};

export const useRoom = (
  eventCode: string,
  {
    ingestServer,
    defaultAudioMuted = true,
    defaultVideoMuted = true,
    onJoinedToStage,
    onHandRaiseApproved,
    onHandRaiseRejected,
    onInvitedToStage,
    onRemovedFromStage,
    onPermissionDenied,
    onMuted,
    onEnded,
    onAudienceLimitIsReached,
    onJoiningDeclined,
    onNetworkQualityChanged,
    onJoinedFromAnotherSession
  }: RoomOptions = {}
): RoomResult => {
  const dispatch = useDispatch();
  const eventSession = useStore(eventSessionSelector(eventCode));
  const toast = useToast();
  const liveEventActions = useLiveEventActions(eventCode);
  const {
    auth,
    eventSessionToken,
    localUser,
    hosts,
    eventStageUsers,
    eventRequests,
    eventViewers,
    isHost,
    isPassiveViewer,
    isHandRaised,
    isHandRaiseEnabled,
    isInvitedToStage,
    activeStreamingPlatforms
  } = useStore(state => ({
    auth: authSelector(state),
    eventSessionToken: eventSessionTokenSelector(eventCode)(state),
    localUser: userSelector(eventCode, eventSession.username)(state),
    hosts: hostsSelector(eventCode)(state),
    eventStageUsers: stageUsersSelector(eventCode)(state),
    eventRequests: requestsSelector(eventCode)(state),
    eventViewers: viewersSelector(eventCode)(state),
    isHost: isHostSelector(eventCode, eventSession.username)(state),
    isPassiveViewer: isPassiveViewerSelector(eventCode, eventSession.username)(state),
    isHandRaised: isHandRaisedSelector(eventCode, eventSession.username)(state),
    isHandRaiseEnabled: isHandRaiseEnabledSelector(eventCode)(state),
    isInvitedToStage: isInvitedToStageSelector(eventCode, eventSession.username)(state),
    activeStreamingPlatforms: activeStreamingPlatformsSelector(state)
  }));

  const [state, setState] = useState<RoomState>({
    ready: false,
    loading: true,
    joined: false,
    audioMuted: defaultAudioMuted,
    videoMuted: defaultVideoMuted,
    facingMode: FacingMode.FRONT,
    layout: Layout.ACTIVE_SPEAKER,
    autoLayoutChange: true,
    activeUser: {
      uid: null,
      pinMode: PinMode.AUTO,
      pinned: false
    }
  });

  const isAuthRef = useRef<boolean>(auth.isAuth);
  const userRoleRef = useRef<UserRole>(localUser.role);

  const rtc = useRTC(eventCode, {
    clientRole: state.joined ? mapUserRoleToAgoraClientRole(localUser.role) : 'audience',
    onChangeActiveUser: useCallback(cb => {
      setState(prev => {
        const updatedActiveUser = cb(prev.activeUser);

        return {
          ...prev,
          activeUser: updatedActiveUser
        };
      });
    }, []),
    onNetworkQualityChanged,
    onClientRoleChanged: newClientRole => {
      if (newClientRole === 'audience') {
        setState(prev => ({ ...prev, joined: false, ready: false, audioMuted: true, videoMuted: true }));
      }
    }
  });

  const rtm = useRTM(eventCode, {
    onRemovedFromStage: async () => {
      setState(prev => ({ ...prev, loading: true }));

      toast.info('You moved back to the audience', { id: 'BACK_TO_AUDIENCE' });

      if (auth.isAuth) {
        onRemovedFromStage?.();
        eventsActions.updateSession({
          eventCode,
          session: {
            username: auth.username || null,
            token: auth.accessToken,
            tokenSource: LiveEventTokenSource.AUTH
          }
        });

        const { data: updatedEventState } = await api.requests.ltv.joinAsViewer(eventCode, {
          name: auth.name,
          avatar: auth.image
        });

        dispatch(eventsActions.updateState(updatedEventState));
      } else {
        const { data: updatedEventState } = await api.requests.ltv.getLatestState(eventCode);

        dispatch(eventsActions.updateState(updatedEventState));
      }

      await rtc.leave();

      setState(prev => ({ ...prev, loading: false, joined: false, ready: false }));
    },
    onRoleSwitched: async moderator => {
      const { data: latestEventState } = await api.requests.ltv.getLatestState(eventCode);

      dispatch(eventsActions.updateState(latestEventState));

      const toastText = moderator ? 'You are now a moderator' : 'Your are now a speaker';

      toast.info(toastText, { id: 'ROLE_SWITCHED' });
    },
    onInviteeRemovedFromStage: async username => {
      const { data: updatedEventState } = await api.requests.ltv.removeFromStage(eventCode, username);

      dispatch(eventsActions.updateState(updatedEventState));
    },
    onInvitedToStage: async () => {
      toast.info(
        'You are about to join the stage. Make sure your video and audio are all set on the back-stage panel.',
        { id: 'YOU_ARE_ABOUT_TO_JOIN' }
      );

      const { data: latestEventState } = await api.requests.ltv.getLatestState(eventCode);

      dispatch(eventsActions.updateState(latestEventState));

      onInvitedToStage?.();
    },
    onHandRaiseApproved: async () => {
      toast.info(
        'You have been invited to the stage. Make sure your video and audio are all set on the back-stage panel.',
        { id: 'HAND_RAISE_REQUEST' }
      );

      const { data: latestEventState } = await api.requests.ltv.getLatestState(eventCode);

      dispatch(eventsActions.updateState(latestEventState));

      onHandRaiseApproved?.();
    },
    onHandRaiseRejected: async () => {
      toast.info('Your request is rejected', { id: 'HAND_RAISE_REQUEST' });

      const { data: latestEventState } = await api.requests.ltv.getLatestState(eventCode);

      dispatch(eventsActions.updateState(latestEventState));

      onHandRaiseRejected?.();
    },
    onHandRaiseEnabled: () => {
      dispatch(eventsActions.toggleHandRaisePermission({ eventCode, enabled: true }));
    },
    onHandRaiseDisabled: () => {
      dispatch(eventsActions.toggleHandRaisePermission({ eventCode, enabled: false }));
    },
    onHandRaised: async () => {
      const hasPermission =
        liveEventActions.hasPermission(LiveEventAction.APPROVE_HAND_RAISE_REQUEST) ||
        liveEventActions.hasPermission(LiveEventAction.REJECT_HAND_RAISE_REQUEST);

      if (hasPermission) {
        const { data: latestEventState } = await api.requests.ltv.getLatestState(eventCode);

        dispatch(eventsActions.updateState(latestEventState));
      }
    },
    onHandDropped: async () => {
      const hasPermission =
        liveEventActions.hasPermission(LiveEventAction.APPROVE_HAND_RAISE_REQUEST) ||
        liveEventActions.hasPermission(LiveEventAction.REJECT_HAND_RAISE_REQUEST);

      if (hasPermission) {
        const { data: latestEventState } = await api.requests.ltv.getLatestState(eventCode);

        dispatch(eventsActions.updateState(latestEventState));
      }
    },
    onAudioMuted: () => {
      rtc.muteMicrophone();

      onMuted?.();

      setState(prev => ({ ...prev, audioMuted: true }));
    },
    onEnded: () => onEnded?.(),
    onMemberLimitReached: () => {
      if (localUser.role === UserRole.VIEWER) {
        onAudienceLimitIsReached?.();
      }
    },
    onJoiningDeclined: async (username: string, name: string, isInvitee: boolean) => {
      try {
        const { data: latestEventState } = await api.requests.ltv.getLatestState(eventCode);

        dispatch(eventsActions.updateState(latestEventState));
      } catch (e) {
        LoggerService.log('ON_JOINING_DECLINED', e);

        const { data: latestEventState } = await api.requests.ltv.getLatestState(eventCode);

        dispatch(eventsActions.updateState(latestEventState));
      }

      toast.info(`${name} declined to join to stage`, { id: `VIEWER_DECLINED_TO_JOIN:${username}` });
      onJoiningDeclined?.(username, name, isInvitee);
    },
    onJoinedFromAnotherSession
  });

  const stageUsers = useMemo(() => {
    const rtcUsersMap: Record<string, RTCUser> = rtc.state.remoteUsers.reduce(
      (target, remoteUser) => ({ ...target, [remoteUser.uid.toString()]: remoteUser }),
      {}
    );
    const rtmUIDsMap: Record<string, string> = rtm.membersUIDs.reduce(
      (target, rtmUID) => ({ ...target, [rtmUID]: rtmUID }),
      {}
    );

    const result: OnStageUser[] = eventStageUsers.map(eventUser => {
      const hashedUID = hashUsername(eventUser.username).toString();
      const rtcUser = rtcUsersMap[hashedUID];

      return mergeEventUserAndRTCUser(eventUser, rtcUser, rtmUIDsMap[hashedUID]);
    });

    if (localUser.role !== UserRole.VIEWER && rtc.uid && state.joined) {
      const localRTCUser: OnStageUser = mergeEventUserAndRTCUser(localUser, {
        uid: rtc.uid as UID,
        hasAudio: !state.audioMuted,
        hasVideo: !state.videoMuted,
        audioTrack: state.joined ? rtc.audioTrack : undefined,
        videoTrack: state.joined ? rtc.videoTrack : undefined,
        isSpeaking: !rtc.audioTrack?.muted && (rtc.audioTrack?.getVolumeLevel() || 0) * 100 > ACTIVE_VOLUME_THRESHOLD
      });

      const idx = result.findIndex(user => user.username === localUser.username);

      result[idx] = { ...result[idx], ...localRTCUser };
    }

    return result;
  }, [
    rtc.uid,
    rtc.audioTrack,
    rtc.videoTrack,
    rtc.state.remoteUsers,
    rtm.membersUIDs,
    state.audioMuted,
    state.videoMuted,
    state.joined,
    localUser,
    eventStageUsers
  ]);

  // all joined users
  const joinedStageUsers = useMemo(() => stageUsers.filter(user => user.joined), [stageUsers]);

  const requests = useMemo(() => {
    const actualRequests = getActualRequests(eventRequests, rtm.membersUIDs);

    return actualRequests;
  }, [eventRequests, rtm.membersUIDs]);

  const viewers = useMemo(() => getActualUsers(eventViewers, rtm.membersUIDs), [eventViewers, rtm.membersUIDs]);

  const activeSpeaker = useMemo(() => {
    // active speaker
    const stageUser = stageUsers.find(user => hashUsername(user.username).toString() === state.activeUser.uid);

    // any joined user
    const joinedStageUser = joinedStageUsers?.[0];

    const host = eventStageUsers.find(user => user.role === UserRole.HOST);
    const hostRTC =
      host &&
      mergeEventUserAndRTCUser(host, {
        uid: '' as UID,
        hasAudio: false,
        hasVideo: false,
        isSpeaking: false
      });

    // If there is an active speaker - show him
    if (stageUser?.joined) {
      return stageUser;
    }

    // If there is someone joined - show him
    if (joinedStageUser) {
      return joinedStageUser;
    }

    // If there is nobody on stage - show the host
    if (host && hostRTC) {
      return hostRTC;
    }

    return null;
  }, [stageUsers, state.activeUser.uid, eventStageUsers, joinedStageUsers]);

  const pinIsAvailable = useMemo(() => stageUsers.length >= 2, [stageUsers]);

  const isGridLayoutAvailable = useMemo(() => {
    const remoteUsersCount = rtc.state.remoteUsers.length;
    const onStageUsersCount = localUser.role === UserRole.VIEWER ? remoteUsersCount : remoteUsersCount + 1;

    return onStageUsersCount >= 2;
  }, [rtc.state.remoteUsers.length, localUser.role]);

  const isPinned = useMemo(() => !!state.activeUser.pinned, [state.activeUser.pinned]);

  const requestsCount = useMemo(() => requests.length, [requests]);

  const membersCount = useMemo(() => rtm.state.membersCount, [rtm.state.membersCount]);

  const totalViewersCount = useMemo(
    () => membersCount - stageUsers.filter(stageUser => stageUser.joined).length,
    [membersCount, stageUsers]
  );

  const anonymousViewersCount = useMemo(() => totalViewersCount - viewers.length, [totalViewersCount, viewers]);

  const serializedTranscodingUsers = JSON.stringify(
    stageUsers.map(({ joined, username, image, videoMuted }) => ({
      joined,
      username,
      image,
      videoMuted
    }))
  );

  const raiseHand = async (): Promise<void> => {
    if (!isHandRaiseEnabled || !localUser) return;

    try {
      tracker.raiseHand(eventCode, true);

      const { data: updatedEventState } = await api.requests.ltv.raiseHand(eventCode);

      rtm.sendChannelMessage({ type: ChannelMessageType.RAISE_HAND });
      tracker.raiseHandSuccess(eventCode);
      dispatch(eventsActions.updateState(updatedEventState));
    } catch (error) {
      LoggerService.log('RAISE_HAND', error);

      if (error instanceof Error) {
        tracker.raiseHandFail(eventCode, error);
      }

      throw error;
    }
  };

  const dropHand = async (): Promise<void> => {
    try {
      const { data: updatedEventState } = await api.requests.ltv.dropHand(eventCode);

      rtm.sendChannelMessage({ type: ChannelMessageType.DROP_HAND });
      dispatch(eventsActions.updateState(updatedEventState));
    } catch (e) {
      LoggerService.log('DROP_HAND', e);

      throw e;
    }
  };

  const pushStreamToCDN = (rtmpURL: string) => rtc.pushStreamToCDN(rtmpURL, stageUsers);

  const stopStreamToCDN = (rtmpURL: string) => rtc.stopStreamToCDN(rtmpURL);

  const publishLocalTracks = async (audioEnabled: boolean = false, videoEnabled: boolean = false): Promise<void> => {
    try {
      await rtc.publishLocalTracks(audioEnabled, videoEnabled);

      setState(prev => ({ ...prev, audioMuted: !audioEnabled, videoMuted: !videoEnabled }));
    } catch (e) {
      LoggerService.log('PUBLISH_LOCAL_TRACKS', e);

      setState(prev => ({
        ...prev,
        audioMuted: true,
        videoMuted: true
      }));

      onPermissionDenied?.();
    }
  };

  const toggleAudio = async () => {
    if (!rtc.state.joined) return;

    if (state.audioMuted) {
      try {
        await rtc.unmuteMicrophone();
        tracker.toggleMicrophoneOnControlPanel(eventCode, true);
        setState(prev => ({ ...prev, audioMuted: false }));
      } catch (error) {
        LoggerService.log('TOGGLE_AUDIO', error);
        onPermissionDenied?.();
      }
    } else {
      try {
        await rtc.muteMicrophone();
        tracker.toggleMicrophoneOnControlPanel(eventCode, false);
        setState(prev => ({ ...prev, audioMuted: true }));
      } catch (error) {
        LoggerService.log('TOGGLE_AUDIO', error);
        onPermissionDenied?.();
      }
    }
  };

  const toggleVideo = async () => {
    if (!rtc.state.joined) return;

    if (state.videoMuted) {
      try {
        await rtc.unmuteCamera();
        tracker.toggleCameraOnControlPanel(eventCode, true);
        setState(prev => ({ ...prev, videoMuted: false }));
      } catch (error) {
        LoggerService.log('TOGGLE_VIDEO', error);
        onPermissionDenied?.();
      }
    } else {
      try {
        await rtc.muteCamera();
        tracker.toggleCameraOnControlPanel(eventCode, false);
        setState(prev => ({ ...prev, videoMuted: true }));
      } catch (error) {
        LoggerService.log('TOGGLE_VIDEO', error);
        onPermissionDenied?.();
      }
    }
  };

  const resumeAudio = () => rtc.resumeAudio();

  const switchCamera = useCallback(async (): Promise<FacingMode> => {
    const facingMode = await rtc.changeFacingMode(
      state.facingMode === FacingMode.FRONT ? FacingMode.REAR : FacingMode.FRONT
    );

    setState(prev => ({ ...prev, facingMode }));

    return facingMode;
  }, [state.facingMode, rtc.changeFacingMode]);

  const changeActiveSpeaker = (username: string) => {
    const pinnedAt = convertDateToTimestamp(new Date());

    setState(prev => {
      const speakerUsername = hashUsername(username).toString();

      return {
        ...prev,
        activeUser: {
          uid: speakerUsername,
          pinMode: PinMode.MANUAL,
          pinnedAt
        }
      };
    });
  };

  const pinActiveSpeaker = useCallback(
    (username: string | null) => {
      setState(prev => {
        if (username === null) {
          const defaultActiveSpeaker = stageUsers.find(user => user.videoTrack || user.audioTrack);
          const defaultUsername = defaultActiveSpeaker
            ? hashUsername(defaultActiveSpeaker?.username).toString()
            : prev.activeUser.uid;

          return {
            ...prev,
            activeUser: { uid: defaultUsername, pinMode: PinMode.AUTO, pinned: false }
          };
        }

        return {
          ...prev,
          activeUser: { uid: hashUsername(username).toString(), pinned: true, pinMode: PinMode.AUTO }
        };
      });
    },
    [stageUsers]
  );

  const changeLayout = useCallback((layout: Layout) => {
    tracker.changeLayout(eventCode, layout);
    setState(prev => ({ ...prev, layout, autoLayoutChange: false }));
  }, []);

  const resetInvitationLink = async (inviteeCode: string): Promise<void> => {
    const { data: updatedEventState } = await api.requests.ltv.removeInvitee(eventCode, inviteeCode);

    dispatch(eventsActions.updateState(updatedEventState));
  };

  const generateInvitationLink = async (
    name: string,
    role: UserRole.SPEAKER | UserRole.MODERATOR
  ): Promise<InviteeSchema> => {
    try {
      tracker.generateInvitationLink(eventCode, name, role);
      const { data: generatedInvitee } = await api.requests.ltv.createInvitee(
        eventCode,
        name,
        role === UserRole.MODERATOR
      );
      const { data: latestEventState } = await api.requests.ltv.getLatestState(eventCode);

      tracker.generateInvitationLinkSuccess(eventCode, name, role);
      dispatch(eventsActions.updateState(latestEventState));

      return generatedInvitee;
    } catch (e) {
      LoggerService.log('GENERATE_INVITATION_LINK', e);

      if (e instanceof Error) {
        tracker.generateInvitationLinkFail(eventCode, name, role, e);
      }

      throw e;
    }
  };

  const switchRole = async (username: string, moderator: boolean): Promise<void> => {
    const { data: updatedEventState } = await api.requests.ltv.changeRole(
      eventCode,
      username,
      moderator ? UserRole.MODERATOR : UserRole.SPEAKER
    );
    let message: PeerMessage;

    if (moderator) message = { type: PeerMessageType.PROMOTE_TO_MODERATOR };
    else message = { type: PeerMessageType.DEMOTE_FROM_MODERATOR };

    dispatch(eventsActions.updateState(updatedEventState));
    rtm.sendPeerMessage(hashUsername(username).toString(), message);
  };

  const removeFromStage = async (username: string, type: OnStageUserType = 'stageUser') => {
    const removeInviteeFromStage = async (): Promise<void> => {
      const uid = hashUsername(username).toString();
      const { data: updatedEventState } = await api.requests.ltv.removeInvitee(eventCode, username);

      dispatch(eventsActions.updateState(updatedEventState));
      await rtm.sendPeerMessage(uid, { type: PeerMessageType.REMOVE_FROM_STAGE });
    };

    const removeSpeakerFromStage = async (): Promise<void> => {
      const uid = hashUsername(username).toString();
      const { data: updatedEventState } = await api.requests.ltv.removeFromStage(eventCode, username);

      dispatch(eventsActions.updateState(updatedEventState));

      await rtm.sendPeerMessage(uid, { type: PeerMessageType.REMOVE_FROM_STAGE });
    };

    const removePendingUser = async (): Promise<void> => {
      const uid = hashUsername(username).toString();
      const { data: updatedEventState } = await api.requests.ltv.removePendingUser(eventCode, username);

      dispatch(eventsActions.updateState(updatedEventState));

      await rtm.sendPeerMessage(uid, { type: PeerMessageType.REMOVE_FROM_STAGE });
    };

    switch (type) {
      case 'stageUser': {
        await removeSpeakerFromStage();
        break;
      }

      case 'invitee': {
        await removeInviteeFromStage();
        break;
      }

      case 'pending': {
        await removePendingUser();
        break;
      }

      default:
        break;
    }
  };

  const inviteToStage = async (username: string): Promise<void> => {
    tracker.inviteToStage(eventCode, username);
    try {
      const peerId = hashUsername(username).toString();
      const { data: updatedEventState } = await api.requests.ltv.inviteViewer(eventCode, username);
      const message: InviteToStageMessage = { type: PeerMessageType.INVITE_TO_STAGE };

      tracker.inviteToStageSuccess(eventCode, username);
      dispatch(eventsActions.updateState(updatedEventState));

      await rtm.sendPeerMessage(peerId, message);
    } catch (e) {
      LoggerService.log('INVITE_VIEWER_TO_STAGE', e);

      if (e instanceof Error) {
        tracker.inviteToStageFail(eventCode, username, e);
      }

      throw e;
    }
  };

  const approveHandRaise = async (username: string): Promise<void> => {
    tracker.approveHandRaise(eventCode, username);
    try {
      const peerId = hashUsername(username).toString();
      const { data: updatedEventState } = await api.requests.ltv.approveHandRaise(eventCode, username);
      const message: ApproveHandRaiseMessage = { type: PeerMessageType.APPROVE_HAND_RAISE };

      tracker.approveHandRaiseSuccess(eventCode, username);
      dispatch(eventsActions.updateState(updatedEventState));

      await rtm.sendPeerMessage(peerId, message);
    } catch (e) {
      LoggerService.log('APPROVE_HAND_RAISE', e);

      if (e instanceof Error) {
        tracker.approveHandRaiseFail(eventCode, username, e);
      }

      throw e;
    }
  };

  const rejectHandRaise = async (username: string): Promise<void> => {
    tracker.rejectHandRaise(eventCode, username);
    try {
      const peerId = hashUsername(username).toString();
      const { data: updatedEventState } = await api.requests.ltv.removeHandRaiseRequest(eventCode, username);
      const message: RejectHandRaiseMessage = { type: PeerMessageType.REJECT_HAND_RAISE };

      tracker.rejectHandRaiseSuccess(eventCode, username);
      dispatch(eventsActions.updateState(updatedEventState));

      await rtm.sendPeerMessage(peerId, message);
    } catch (e) {
      LoggerService.log('REJECT_HAND_RAISE', e);

      if (e instanceof Error) {
        tracker.rejectHandRaiseFail(eventCode, username, e);
      }

      throw e;
    }
  };

  const enableHandRaise = async (): Promise<void> => {
    const message: ChannelMessage = { type: ChannelMessageType.ENABLE_HAND_RAISE } as EnableHandRaiseMessage;

    await rtm.sendChannelMessage(message);
  };

  const disableHandRaise = async (): Promise<void> => {
    const message: ChannelMessage = { type: ChannelMessageType.DISABLE_HAND_RAISE } as DisableHandRaiseMessage;

    await rtm.sendChannelMessage(message);
  };

  const forceMute = async (username: string): Promise<void> => {
    const peerId = hashUsername(username).toString();
    const message: MuteAudioMessage = { type: PeerMessageType.MUTE_AUDIO };

    await rtm.sendPeerMessage(peerId, message);
  };

  const recoverCDNStreams = () => {
    Promise.all([
      ...activeStreamingPlatforms
        .filter(platform => !platform.stopped && platform.url)
        .map(platform => () => rtc.pushStreamToCDN(platform.url as string, stageUsers))
        .map(promise => promise())
    ]);
  };

  const stopCDNStreams = async () => {
    let promises: Promise<void>[] = [];

    if (ingestServer) {
      promises = [rtc.stopStreamToCDN(ingestServer)];
    }

    promises = [
      ...promises,
      ...activeStreamingPlatforms
        .filter(platform => platform.url)
        .map(platform => () => rtc.stopStreamToCDN(platform.url as string))
        .map(promise => promise())
    ];

    await Promise.all(promises);
  };

  const end = async () => {
    try {
      if (!hosts?.[0]) return;

      tracker.endEvent(eventCode);

      const { data: updatedEventState } = await api.requests.ltv.endEvent(eventCode);

      tracker.endEventSuccess(eventCode);

      await rtm.sendChannelMessage({ type: ChannelMessageType.END_EVENT });
      await stopCDNStreams();

      dispatch(eventsActions.updateState(updatedEventState));

      rtm.leave();
      rtc.leave();
    } catch (error) {
      LoggerService.log('END_LIVE_EVENT', error);

      if (error instanceof Error) {
        tracker.endEventFail(eventCode, error);
      }
    }
  };

  /**
   * There are 4 cases for decline to join:
   *  - Invitee declines invitation [covered]
   *  - Viewer declines when his/her hand-raise request is accepted [covered]
   *  - Viewer declines when he/she is invited to stage by host [covered]
   *  - Existing speaker declines when he/she is rejoining (refreshing the tab) [covered]
   */
  const declineJoining = async (isInvitee: boolean) => {
    tracker.declineToJoin(eventCode, localUser.username);
    try {
      const isStageUser = auth.isAuth && !isInvitee;

      const peerId = hashUsername(hosts[0].username).toString();

      await rtc.leave();

      // rtm message is sent to host in any case so as to notify
      await rtm.sendPeerMessage(peerId, {
        type: PeerMessageType.DECLINE_JOINING,
        payload: { username: localUser.username, name: localUser.name, isInvitee }
      });

      // Invitee declines invitation
      if (isInvitee && eventSessionToken) {
        const { data: updatedEventState } = await api.requests.ltv.declineInvitation(eventCode);

        tracker.declineToJoinSuccess(eventCode, localUser.username);
        dispatch(eventsActions.updateState(updatedEventState));
      }

      // at this point, it is guaranteed that user is authenticated
      // invited to stage by host/moderator
      // OR hand raise is approved
      else if (isInvitedToStage) {
        const { data: updatedEventState } = await api.requests.ltv.declineInviteRequest(eventCode);

        tracker.declineToJoinSuccess(eventCode, localUser.username);
        dispatch(eventsActions.updateState(updatedEventState));
      } else if (isStageUser) {
        const { data: updatedEventState } = await api.requests.ltv.leaveStage(eventCode);

        tracker.declineToJoinSuccess(eventCode, localUser.username);
        dispatch(eventsActions.updateState(updatedEventState));
      }
    } catch (error) {
      LoggerService.log('DECLINE_TO_JOIN_THE_STAGE', error);

      if (error instanceof Error) {
        tracker.declineToJoinFail(eventCode, localUser.username, error);
        toast.error(error.message, { id: 'DECLINE_TO_JOIN_THE_STAGE' });
      }

      throw error;
    } finally {
      setState(prev => ({
        ...prev,
        audioMuted: true,
        videoMuted: true,
        joined: false,
        ready: false,
        facingMode: FacingMode.FRONT
      }));
    }
  };

  const RTCToken = useRef<string | null>(null);
  const RTMToken = useRef<string | null>(null);

  const joinRoom = async () => {
    setState(prev => ({ ...prev, loading: true }));

    try {
      const {
        data: { response: token }
      } = await api.requests.ltv.getRTMToken(eventSession.uid);

      RTMToken.current = token;
      rtm.join(eventSession.uid.toString(), RTMToken.current);
      setState(prev => ({ ...prev, loading: false }));
    } catch (e) {
      LoggerService.log('JOIN_AGORA_RTM_CHANNEL', e);
    }
  };

  const preview = async () => {
    if (config.app.env === 'production') {
      const {
        data: { response: token }
      } = await api.requests.ltv.getRTCToken(eventCode, eventSession.uid);

      RTCToken.current = token;
    }

    setState(prev => ({ ...prev, ready: false }));
    await rtc.join(eventSession.uid, RTCToken.current);
    setState(prev => ({ ...prev, ready: true }));
  };

  const joinStage = async () => {
    if (config.app.env === 'production' && !RTCToken.current) {
      const {
        data: { response: token }
      } = await api.requests.ltv.getRTCToken(eventCode, eventSession.uid);

      RTCToken.current = token;
    }

    try {
      setState(prev => ({ ...prev, joined: false }));

      if (!rtc.state.joined) {
        await rtc.join(eventSession.uid, RTCToken.current);
        setState(prev => ({ ...prev, ready: true }));
      }

      // set the first speaker as active initially
      if (stageUsers.length > 0) {
        changeActiveSpeaker(stageUsers[0].username);
      } else if (localUser.role !== UserRole.VIEWER) {
        changeActiveSpeaker(localUser.username);
      }

      if (isHost && ingestServer) {
        pushStreamToCDN(ingestServer);
      }

      if (localUser.role === UserRole.HOST) {
        recoverCDNStreams();
      }

      setState(prev => ({ ...prev, joined: true }));

      onJoinedToStage?.();
    } catch (e) {
      LoggerService.log('JOIN_THE_STAGE', e);

      throw e;
    }
  };

  const leaveStage = async (withInviteeCode?: boolean) => {
    try {
      tracker.leaveStage(eventCode);

      if (withInviteeCode) {
        eventsActions.updateSession({
          eventCode,
          session: {
            username: auth.username || null,
            token: auth.accessToken,
            tokenSource: LiveEventTokenSource.AUTH
          }
        });

        const peerId = hashUsername(hosts[0].username).toString();

        if (eventSession.username) {
          await rtm.sendPeerMessage(peerId, {
            type: PeerMessageType.REMOVE_INVITEE_FROM_STAGE,
            payload: { username: eventSession.username }
          });

          const { isAuth, name, image } = auth;

          if (isAuth) {
            await api.requests.ltv.joinAsViewer(eventCode, { name, avatar: image });
          }
        }
      } else {
        const { data: updatedEventState } = await api.requests.ltv.leaveStage(eventCode);

        dispatch(eventsActions.updateState(updatedEventState));
      }

      await rtc.leave();

      setState(prev => ({ ...prev, audioMuted: true, videoMuted: true, joined: false, ready: false }));

      tracker.leaveStageSuccess(eventCode);
    } catch (e) {
      LoggerService.log('LEAVE_THE_STAGE', e);

      if (e instanceof Error) {
        tracker.leaveStageFail(eventCode, e);
      }

      throw e;
    }
  };

  const leaveEvent = async () => {
    const { data: updatedEventState } = await api.requests.ltv.leaveEvent(eventCode);

    dispatch(eventsActions.updateState(updatedEventState));
    setState(prev => ({ ...prev, audioMuted: true, videoMuted: true, joined: false, ready: false }));
  };

  const throttledUpdateTranscoding = useCallback(
    // SOURCE: https://ellenaua.medium.com/throttle-debounce-behavior-lodash-6bcae1494e03
    _throttle((users: OnStageUser[]) => rtc.updateTranscodingConfig(users), 5_000, {
      leading: false,
      trailing: true
    }),
    []
  );

  // join/leave effect
  useEffect(() => {
    joinRoom();

    return () => {
      rtc.leave();
      rtm.leave();
    };
  }, [eventCode, eventSession.uid]);

  useEffect(() => {
    setState(prev => ({ ...prev, audioMuted: defaultAudioMuted }));
  }, [defaultAudioMuted]);

  useEffect(() => {
    setState(prev => ({ ...prev, videoMuted: defaultVideoMuted }));
  }, [defaultVideoMuted]);

  // TODO: workaround to avoid useEffect's cleanup memoization issue
  useEffect(() => {
    isAuthRef.current = auth.isAuth;
  }, [auth.isAuth]);

  // TODO: workaround to avoid useEffect's cleanup memoization issue
  useEffect(() => {
    userRoleRef.current = localUser.role;
  }, [localUser.role]);

  // set audio/video muted states to true when user becomes viewer effect
  useEffect(() => {
    if (localUser.role === UserRole.VIEWER) {
      setState(prev => ({ ...prev, videoMuted: true, audioMuted: true }));
    }
  }, [localUser.role]);

  useEffect(() => {
    if (rtc.state.joined && isHost) {
      throttledUpdateTranscoding(stageUsers);
    }

    return () => {
      throttledUpdateTranscoding.cancel();
    };
  }, [serializedTranscodingUsers, rtc.state.remoteUsers.length, isHost, rtc.state.joined, rtc.state.published]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!(!!event.returnValue && event.returnValue !== '')) {
        if (isAuthRef.current && userRoleRef.current === UserRole.VIEWER) {
          leaveEvent();
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  return {
    ready: state.ready,
    loading: state.loading,
    joining: rtc.state.joining || rtm.state.joining,
    joined: state.joined,
    isPinned,
    pinIsAvailable,
    isPassiveViewer,
    isGridLayoutAvailable,
    handRaised: isHandRaised,
    facingMode: state.facingMode,
    layout: state.layout,
    membersCount,
    requestsCount,
    totalViewersCount,
    anonymousViewersCount,
    audioMuted: state.audioMuted,
    videoMuted: state.videoMuted,
    activeSpeaker,
    stageUsers,
    requests,
    viewers,
    preview,
    joinStage,
    leaveStage,
    leaveEvent,
    raiseHand,
    dropHand,
    pushStreamToCDN,
    stopStreamToCDN,
    publishLocalTracks,
    toggleAudio,
    toggleVideo,
    switchCamera,
    changeActiveSpeaker,
    pinActiveSpeaker,
    changeLayout,
    resetInvitationLink,
    generateInvitationLink,
    switchRole,
    removeFromStage,
    inviteToStage,
    approveHandRaise,
    rejectHandRaise,
    enableHandRaise,
    disableHandRaise,
    forceMute,
    end,
    declineJoining,
    resumeAudio
  };
};
